import { useState, useEffect, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
  Search,
  Check,
  PlayCircle,
  Star,
  ChevronDown,
  ClipboardList,
  Megaphone,
  Zap,
  Activity,
  Library,
  Gift,
  MoreVertical,
  ArrowLeft,
} from 'lucide-react'
import Layout from '../../components/Layout/Layout'
import { getDetailedMarketData } from '../../services/tradeService'

import '../../styles/dashboard.css'
import '../../styles/market.css'

// High-fidelity SVG components for Crypto Logos
const BTCLogo = () => (
  <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="16" cy="16" r="16" fill="#F7931A" />
    <path d="M22.962 14.244c.401-2.68-1.64-4.12-4.43-5.08l.904-3.626-2.207-.55-.88 3.532c-.58-.145-1.177-.281-1.77-.417l.886-3.553-2.207-.55-.904 3.626c-.48-.11-.95-.216-1.411-.328l.002-.007-3.044-.76-.587 2.356s1.638.375 1.604.398c.894.223 1.056.815 1.028 1.284l-1.03 4.133c.062.015.142.037.23.072-.074-.019-.153-.038-.232-.057l-1.443 5.79c-.11.272-.388.68-1.015.524.023.033-1.604-.4-1.604-.4l-1.096 2.527 2.872.716c.534.133 1.058.272 1.576.401l-.912 3.664 2.207.551.904-3.626c.602.164 1.188.311 1.761.45l-.893 3.582 2.207.551.912-3.664c3.764.713 6.594.425 7.785-2.978.96-2.74-.047-4.322-2.025-5.353 1.44-.333 2.524-1.282 2.812-3.243zm-5.035 7.087c-.683 2.743-5.3 1.26-6.8 .886l1.213-4.86c1.5.374 6.3 1.114 5.587 3.974zm.683-7.123c-.622 2.497-4.467 1.228-5.719.917l1.1-4.414c1.25.311 5.258.892 4.619 3.497z" fill="white" />
  </svg>
)

const ETHLogo = () => (
  <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="16" cy="16" r="16" fill="#627EEA" />
    <path d="M16 4.667l-.23.784v13.064l.23.23 6.01-3.553L16 4.667z" fill="white" fillOpacity="0.602" />
    <path d="M16 4.667L9.99 15.192l6.01 3.553V4.667z" fill="white" />
    <path d="M16 20.155l-.13.158v6.784l.13.38 6.014-8.472-6.014 1.15z" fill="white" fillOpacity="0.602" />
    <path d="M16 27.477V20.155L9.99 19.006l6.01 8.471z" fill="white" />
    <path d="M16 18.745l6.01-3.553L16 12.607v6.138z" fill="white" fillOpacity="0.2" />
    <path d="M9.99 15.192l6.01 3.553v-6.138l-6.01 2.585z" fill="white" fillOpacity="0.602" />
  </svg>
)

const AVAXLogo = () => (
  <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="16" cy="16" r="16" fill="#E84142" />
    <path d="M18.814 11.234l1.137 1.968c-.979.809-1.396 1.487-1.442 2.373a2.768 2.768 0 00.323 1.579c.277.47.67.85 1.137 1.099.646.346 1.34.46 2.03.323.231-.046.462-.115.67-.208l1.13 1.962c-.624.462-1.316.786-2.078.946a7.18 7.18 0 01-3.116-.185c-1.454-.531-2.61-1.571-3.233-2.934a6.593 6.593 0 01-.139-4.85c.577-1.478 1.709-2.61 3.116-3.134 1.431-.555 2.979-.555 4.38.01l-1.138 1.964c-.878-.44-1.85-.509-2.77-.208a3.172 3.172 0 00-1.87 1.282c-.37.531-.555 1.156-.555 1.778 0 .162.023.324.046.462l3.43-5.942z" fill="white" />
    <path fillRule="evenodd" clipRule="evenodd" d="M16 27L10.334 17.185L4.667 27H16zM18.835 17.185L13.167 7.37l-5.666 9.815h11.334zM27.334 27h-11.334l5.667-9.815L27.334 27z" fill="white" />
  </svg>
)

const SOLLogo = () => (
  <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="16" cy="16" r="16" fill="black" />
    <path d="M24.71 19.332a.5.5 0 0 1-.353.147H7.7a.5.5 0 0 1-.354-.853l3.078-3.078a.5.5 0 0 1 .354-.147H27.7a.5.5 0 0 1 .353.853l-3.344 3.33zm-.001-9.993a.5.5 0 0 1-.353.147H7.7a.5.5 0 0 1-.354-.853l3.078-3.078a.5.5 0 0 1 .354-.147H27.7a.5.5 0 0 1 .354.854l-3.345 3.328zm-20.063 6.643a.5.5 0 0 1 .354-.147h16.657a.5.5 0 0 1 .353.853l-3.078 3.079a.5.5 0 0 1-.354.147H7.29a.5.5 0 0 1-.353-.853l3.072-3.079z" fill="url(#paint0_linear)" />
    <defs>
      <linearGradient id="paint0_linear" x1="4.29" y1="21.751" x2="28.054" y2="5.402" gradientUnits="userSpaceOnUse">
        <stop stopColor="#00FFA3" />
        <stop offset="1" stopColor="#DC1FFF" />
      </linearGradient>
    </defs>
  </svg>
)

const XRPLogo = () => (
  <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="16" cy="16" r="16" fill="#23292F" />
    <path d="M23.5 8h2.5l-6.3 6.2c-2 1.9-5.2 1.9-7.1 0L6.2 8H8.8l5 4.9c1.2 1.2 3.2 1.2 4.4 0L23.5 8zM8.7 24H6.2l6.4-6.2c2-1.9 5.2-1.9 7.1 0L26 24h-2.5l-5-4.9c-1.2-1.2-3.2-1.2-4.4 0L8.7 24z" fill="white" />
  </svg>
)

const DOGELogo = () => (
  <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="16" cy="16" r="16" fill="#C2A633" />
    <text x="8" y="21" fontSize="14" fill="white" fontWeight="bold">Ð</text>
  </svg>
)

const BNBLogo = () => (
  <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="16" cy="16" r="16" fill="#F3BA2F" />
    <path d="M12.116 14.404L16 10.52l3.886 3.886 2.26-2.26L16 6l-6.144 6.144 2.26 2.26zM6 16l2.26-2.26L10.52 16l-2.26 2.26L6 16zm6.116 1.596L16 21.48l3.886-3.886 2.26 2.259L16 26l-6.144-6.144-.002-.003 2.262-2.257zM21.48 16l2.26-2.26L26 16l-2.26 2.26L21.48 16zm-3.188-.002h.002V16L16 18.294 13.708 16l-.001-.001v-.002L16 13.706l2.292 2.292z" fill="white" />
  </svg>
)

function Market() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const view = searchParams.get('view') || 'dashboard'
  const tabParam = searchParams.get('tab') as 'favorites' | 'spot' | 'futures' | null
  const [activeTab, setActiveTab] = useState<'favorites' | 'spot' | 'futures'>(tabParam || 'spot')
  const [mobileMarketTab, setMobileMarketTab] = useState<'favorites' | 'spot' | 'futures'>(tabParam || 'spot')
  const [futuresSubTab, setFuturesSubTab] = useState<'all' | 'usdt-m' | 'coin-m'>('all')
  const [mobileHotTab, setMobileHotTab] = useState<'hot' | 'new' | '24h'>('hot')
  const [marketData, setMarketData] = useState<Record<string, any>>({})
  const [isPricesLoading, setIsPricesLoading] = useState(true)
  const [spotSearchQuery, setSpotSearchQuery] = useState('')
  const SPOT_PAGE_SIZE = 20
  const [spotVisibleCount, setSpotVisibleCount] = useState(SPOT_PAGE_SIZE)
  const spotSentinelRef = useRef<HTMLDivElement | null>(null)
  const [selectedFavorites, setSelectedFavorites] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('bicoin_market_favorites')
      if (saved) return new Set(JSON.parse(saved))
    } catch { /* fall through to default */ }
    return new Set(['BTC', 'ETH', 'SOL', 'BNB', 'XRP', 'AVAX'])
  })

  const allSymbols = ['AAVE/USDT', 'ADA/USDT', 'AGIX/USDT', 'ALGO/USDT', 'APT/USDT', 'ARB/USDT', 'ATOM/USDT', 'AVAX/USDT', 'AXS/USDT', 'BAT/USDT', 'BCH/USDT', 'BLUR/USDT', 'BNB/USDT', 'BONK/USDT', 'BTC/USDT', 'CFX/USDT', 'CHZ/USDT', 'COMP/USDT', 'CRV/USDT', 'CVX/USDT', 'DOGE/USDT', 'DOT/USDT', 'DYDX/USDT', 'EGLD/USDT', 'ENJ/USDT', 'ETC/USDT', 'ETH/USDT', 'FET/USDT', 'FIL/USDT', 'FLOKI/USDT', 'FTM/USDT', 'GALA/USDT', 'GMT/USDT', 'GRT/USDT', 'HBAR/USDT', 'HOT/USDT', 'ICP/USDT', 'IMX/USDT', 'INJ/USDT', 'JUP/USDT', 'KAS/USDT', 'LINK/USDT', 'LRC/USDT', 'LTC/USDT', 'MAGIC/USDT', 'MANA/USDT', 'MKR/USDT', 'NEAR/USDT', 'NOT/USDT', 'OCEAN/USDT', 'ONE/USDT', 'OP/USDT', 'PEPE/USDT', 'RENDER/USDT', 'ROSE/USDT', 'RUNE/USDT', 'SAND/USDT', 'SEI/USDT', 'SHIB/USDT', 'SNX/USDT', 'SOL/USDT', 'STX/USDT', 'SUI/USDT', 'THETA/USDT', 'TIA/USDT', 'TON/USDT', 'TRX/USDT', 'UNI/USDT', 'VET/USDT', 'WIF/USDT', 'WLD/USDT', 'XRP/USDT', 'YFI/USDT', 'ZIL/USDT', 'ZRX/USDT', '1INCH/USDT', 'ACE/USDT', 'ACH/USDT', 'AERGO/USDT', 'AEVO/USDT', 'AGLD/USDT', 'AIXBT/USDT', 'ALICE/USDT', 'ALPACA/USDT', 'ALPHA/USDT', 'ALT/USDT', 'AMB/USDT', 'ANIME/USDT', 'ANKR/USDT', 'APE/USDT', 'API3/USDT', 'AR/USDT', 'ARC/USDT', 'ARK/USDT', 'ARKM/USDT', 'ASR/USDT', 'ASTR/USDT', 'ATA/USDT', 'AUCTION/USDT', 'AUDIO/USDT', 'AURA/USDT', 'BAL/USDT', 'BANANA/USDT', 'BAND/USDT', 'BB/USDT', 'BEL/USDT', 'BERA/USDT', 'BETA/USDT', 'BICO/USDT', 'BIFI/USDT', 'BIO/USDT', 'BLAST/USDT', 'BLZ/USDT', 'BNT/USDT', 'BOME/USDT', 'BSW/USDT', 'C98/USDT', 'CAKE/USDT', 'CAT/USDT', 'CATI/USDT', 'CELO/USDT', 'CELR/USDT', 'CET/USDT', 'CGPT/USDT', 'CHR/USDT', 'CKB/USDT', 'COMBO/USDT', 'COOKIE/USDT', 'COS/USDT', 'COTI/USDT', 'CTSI/USDT', 'CVC/USDT', 'CYBER/USDT', 'DAR/USDT', 'DASH/USDT', 'DATA/USDT', 'DEGO/USDT', 'DENT/USDT', 'DEXE/USDT', 'DGB/USDT', 'DIA/USDT', 'DODO/USDT', 'DOGS/USDT', 'DUSK/USDT', 'DYM/USDT', 'EDU/USDT', 'EIGEN/USDT', 'ELF/USDT', 'ENA/USDT', 'ENS/USDT', 'EOS/USDT', 'ETHFI/USDT', 'ETHW/USDT', 'FIDA/USDT', 'FIO/USDT', 'FLM/USDT', 'FLOW/USDT', 'FORTH/USDT', 'FRONT/USDT', 'FUEL/USDT', 'FXS/USDT', 'GAS/USDT', 'GHST/USDT', 'GLM/USDT', 'GLMR/USDT', 'GMX/USDT', 'GPS/USDT', 'GRASS/USDT', 'HIFI/USDT', 'HIGH/USDT', 'HIPPO/USDT', 'HIVE/USDT', 'HMSTR/USDT', 'HOOK/USDT', 'HYPE/USDT', 'ICX/USDT', 'ID/USDT', 'IDEX/USDT', 'ILV/USDT', 'INIT/USDT', 'IO/USDT', 'IOST/USDT', 'IOTA/USDT', 'IOTX/USDT', 'IQ/USDT', 'JASMY/USDT', 'JOE/USDT', 'JTO/USDT', 'KAITO/USDT', 'KAVA/USDT', 'KEY/USDT', 'KNC/USDT', 'KSM/USDT', 'LAZIO/USDT', 'LDO/USDT', 'LEVER/USDT', 'LINA/USDT', 'LISTA/USDT', 'LOKA/USDT', 'LPT/USDT', 'LQTY/USDT', 'LSK/USDT', 'LTO/USDT', 'LUMIA/USDT', 'MANTA/USDT', 'MASK/USDT', 'MATIC/USDT', 'MBOX/USDT', 'MDT/USDT', 'ME/USDT', 'MEME/USDT', 'MERL/USDT', 'METIS/USDT', 'MEW/USDT', 'MINA/USDT', 'MOVE/USDT', 'MTL/USDT', 'MYRO/USDT', 'NEIRO/USDT', 'NEO/USDT', 'NFP/USDT', 'NIL/USDT', 'NKN/USDT', 'NMR/USDT', 'NTRN/USDT', 'OG/USDT', 'OGN/USDT', 'OM/USDT', 'OMG/USDT', 'ONDO/USDT', 'ONT/USDT', 'ORCA/USDT', 'ORDI/USDT', 'OSMO/USDT', 'PENDLE/USDT', 'PENGU/USDT', 'PHA/USDT', 'PHB/USDT', 'PIXEL/USDT', 'POL/USDT', 'PONKE/USDT', 'POPCAT/USDT', 'PORTAL/USDT', 'PROM/USDT', 'PUNDIX/USDT', 'PYTH/USDT', 'QTUM/USDT', 'QUICK/USDT', 'RAD/USDT', 'RARE/USDT', 'RAY/USDT', 'RED/USDT', 'REEF/USDT', 'REN/USDT', 'REZ/USDT', 'RLC/USDT', 'RSR/USDT', 'RVN/USDT', 'SAGA/USDT', 'SCR/USDT', 'SFP/USDT', 'SIREN/USDT', 'SKL/USDT', 'SLP/USDT', 'SPELL/USDT', 'SSV/USDT', 'STEEM/USDT', 'STG/USDT', 'STORJ/USDT', 'STRK/USDT', 'SUN/USDT', 'SUPER/USDT', 'SUSHI/USDT', 'SWARMS/USDT', 'SXP/USDT', 'SYN/USDT', 'SYS/USDT', 'TAO/USDT', 'TLM/USDT', 'TNSR/USDT', 'TOKEN/USDT', 'TRB/USDT', 'TROY/USDT', 'TURBO/USDT', 'TWT/USDT', 'UMA/USDT', 'USTC/USDT', 'VANA/USDT', 'VELO/USDT', 'VIRTUAL/USDT', 'VOXEL/USDT', 'WAN/USDT', 'WOO/USDT', 'XAI/USDT', 'XEC/USDT', 'XLM/USDT', 'XMR/USDT', 'XTZ/USDT', 'XVG/USDT', 'YGG/USDT', 'ZEC/USDT', 'ZEN/USDT', 'ZETA/USDT', 'ZEUS/USDT', 'ZIG/USDT', 'ZK/USDT', 'ZRO/USDT']

  // Facebook-style infinite scroll effect is declared further down, after
  // spotHasMore is computed (needs it as a dependency).

    useEffect(() => {
        console.log('%c[Market.tsx] BUILD_MARKER_V2 loaded — self-heal fetch active', 'background:#0f0;color:#000;font-size:16px;padding:4px')
        let ws: WebSocket | null = null;
        let retryTimeout: any;
      let fallbackPollInterval: any;
      let isWsConnected = false;

      const mergeMarketSnapshot = (snapshot: Record<string, any>) => {
        setMarketData(prev => {
          const next = { ...prev };
          let changed = false;

          allSymbols.forEach((symbol) => {
            const data = snapshot[symbol];
            if (!data || typeof data.price !== 'number' || data.price <= 0) return;

            const existing = next[symbol] || {};
            const merged = {
              ...existing,
              price: data.price,
              change24h: typeof data.change24h === 'number' ? data.change24h : (existing.change24h || 0),
              volume24h: typeof data.volume24h === 'number' ? data.volume24h : (existing.volume24h || 0),
              high24h: typeof data.high24h === 'number' ? data.high24h : existing.high24h,
              low24h: typeof data.low24h === 'number' ? data.low24h : existing.low24h,
            };

            if (
              existing.price !== merged.price ||
              existing.change24h !== merged.change24h ||
              existing.volume24h !== merged.volume24h ||
              existing.high24h !== merged.high24h ||
              existing.low24h !== merged.low24h
            ) {
              next[symbol] = merged;
              changed = true;
            }
          });

          if (changed) setIsPricesLoading(false);
          return changed ? next : prev;
        });
      };

      const fetchFallbackMarketData = async () => {
        try {
          console.log(`[Market] fetchFallbackMarketData: requesting ${allSymbols.length} symbols via backend`)
          // Route through our backend (server-to-server call to Binance/CoinGecko/Yahoo)
          // instead of calling api.binance.com directly from the browser. Direct
          // browser→Binance calls require Binance to grant CORS to the user's exact
          // origin, which it doesn't reliably do for some networks/regions — this
          // was causing the page to go permanently blank for anyone in that situation.
          // The backend already does this same thing successfully for the Trade page.
          const data = await getDetailedMarketData(allSymbols)
          console.log(`[Market] fetchFallbackMarketData: got ${Object.keys(data).length} symbols from backend`)
          mergeMarketSnapshot(data)
        } catch (outerErr) {
          console.log('[Market] fetchFallbackMarketData: outer catch (unexpected):', outerErr)
          // Keep existing values on transient network failures.
        }
      };

        const connectWS = () => {
            try {
                ws = new WebSocket('wss://stream.binance.com:9443/ws/!ticker@arr');
          ws.onopen = () => { isWsConnected = true; };
                ws.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);
                        if (Array.isArray(data)) {
                            setMarketData(prev => {
                                const next = { ...prev };
                                let changed = false;
                                data.forEach((ticker: any) => {
                                    if (ticker.s.endsWith('USDT')) {
                                        const cleanPair = ticker.s.replace('USDT', '/USDT');
                                        if (allSymbols.includes(cleanPair)) {
                                            const p = parseFloat(ticker.c);
                                            const changePct = parseFloat(ticker.P);
                                            
                                            if (!next[cleanPair] || next[cleanPair].price !== p) {
                                                next[cleanPair] = {
                                                    ...(next[cleanPair] || {}),
                                                    price: p,
                                                    change24h: changePct,
                                                    volume24h: parseFloat(ticker.q) || (next[cleanPair]?.volume24h)
                                                };
                                                changed = true;
                                            }
                                        }
                                    }
                                });
                                return changed ? next : prev;
                            });
                            if (data.some((t: any) => t.s?.endsWith('USDT'))) setIsPricesLoading(false);
                        }
                    } catch (e) { /* ignore */ }
                };
                        ws.onerror = () => { isWsConnected = false; if (ws) ws.close(); };
                        ws.onclose = () => { isWsConnected = false; retryTimeout = setTimeout(connectWS, 5000); };
            } catch (err) {
                        isWsConnected = false;
                retryTimeout = setTimeout(connectWS, 5000);
            }
        };

                    fetchFallbackMarketData();
                    fallbackPollInterval = setInterval(() => {
                      if (!isWsConnected) fetchFallbackMarketData();
                    }, 12000);

        connectWS();
        // Safety net: if neither WS nor the fallback ever delivers anything (total
        // failure on both paths), stop showing the skeleton after 10s rather than
        // leaving it spinning forever — falls back to whatever placeholder state
        // the table already handles.
        const loadingSafetyTimeout = setTimeout(() => setIsPricesLoading(false), 10000);
        return () => {
            clearTimeout(retryTimeout);
                      clearInterval(fallbackPollInterval);
            clearTimeout(loadingSafetyTimeout);
            if (ws) {
                ws.onclose = null;
                ws.onerror = null;
                ws.close();
            }
        };
    }, []);

      const fmt = (n: number, digits = 2) => n ? "$" + n.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits }) : '—'
    const fmtChange = (n: number) => n >= 0 ? "+" + n.toFixed(2) + '%' : n.toFixed(2) + '%'
    const fmtVol = (n: number) => n > 1e9 ? "$" + (n / 1e9).toFixed(2) + 'B' : n > 1e6 ? "$" + (n / 1e6).toFixed(2) + 'M' : "$" + n.toLocaleString()

    const logoMap: Record<string, any> = { BTC: BTCLogo, ETH: ETHLogo, AVAX: AVAXLogo, SOL: SOLLogo, XRP: XRPLogo, DOGE: DOGELogo, BNB: BNBLogo }

  const coinLabels: Record<string, string> = { BTC: 'Bitcoin', ETH: 'Ethereum', XRP: 'Ripple', SOL: 'Solana', AVAX: 'Avalanche', DOGE: 'Dogecoin', BNB: 'Binance', ADA: 'Cardano', DOT: 'Polkadot', LINK: 'Chainlink', LTC: 'Litecoin', NEAR: 'NEAR', APT: 'Aptos', ATOM: 'Cosmos', UNI: 'Uniswap', OP: 'Optimism', ARB: 'Arbitrum', MATIC: 'Polygon' }
  const coinColors: Record<string, string> = { BTC: '#F7931A', ETH: '#627EEA', XRP: '#23292F', SOL: '#9945FF', AVAX: '#E84142', DOGE: '#C2A633', BNB: '#F3BA2F', ADA: '#0033AD', DOT: '#E6007A', LINK: '#2A5ADA', LTC: '#345D9D', NEAR: '#000', APT: '#4EB4A0', ATOM: '#2E3148', UNI: '#FF007A', OP: '#FF0420', ARB: '#28A0F0', MATIC: '#8247E5' }
  // Mock volume multipliers per coin for realistic data
  const mockVolMultiplier: Record<string, number> = { BTC: 28.4, ETH: 14.7, XRP: 3.2, SOL: 5.8, AVAX: 1.9, DOGE: 4.1, BNB: 8.3, ADA: 2.4, DOT: 1.6, LINK: 2.1, LTC: 1.8, NEAR: 0.9, APT: 0.7, ATOM: 1.1, UNI: 1.3, OP: 0.8, ARB: 0.6, MATIC: 1.5 }

  const priceCards = ['BTC/USDT', 'ETH/USDT', 'AVAX/USDT', 'SOL/USDT'].map(s => {
    const coin = s.split('/')[0]
    const d = marketData[s] || {}
    return { coin, pair: 'USDT', price: fmt(d.price), change: fmtChange(d.change24h || 0), volume: `24h Vol ${fmtVol(d.volume24h || 0)}`, Logo: logoMap[coin] || BTCLogo }
  })

  const favoritesData = [
    { coin: 'BTC', pair: 'USDT', label: 'Bitcoin' },
    { coin: 'ETH', pair: 'USDT', label: 'Ethereum' },
    { coin: 'SOL', pair: 'USDT', label: 'Solana' },
    { coin: 'BNB', pair: 'USDT', label: 'Binance' },
    { coin: 'XRP', pair: 'USDT', label: 'Ripple' },
    { coin: 'AVAX', pair: 'USDT', label: 'Avalanche' },
  ]
  const favoriteLabels: Record<string, string> = Object.fromEntries(favoritesData.map(f => [f.coin, f.label]))
  // The actual current favorites (from the star toggle), for the desktop Favorites
  // tab content — distinct from favoritesData above, which stays as the fixed
  // candidate list used by the mobile favorites-editor's "Add all"/individual toggles.
  const activeFavoritesData = [...selectedFavorites].sort().map(coin => ({
    coin, pair: 'USDT', label: favoriteLabels[coin] || coin,
  }))

  const spotTableData = allSymbols
    .map(s => {
    const coin = s.split('/')[0]
    const d = marketData[s] || {}
    const price = d.price || 0
    const change = d.change24h || 0
    const vol = d.volume24h || (price * (mockVolMultiplier[coin] || 1) * 1000000)
    const high = typeof d.high24h === 'number' && d.high24h > 0 ? d.high24h : price * (1 + Math.abs(change || 1.5) / 100 * 1.2)
    const low = typeof d.low24h === 'number' && d.low24h > 0 ? d.low24h : price * (1 - Math.abs(change || 1.5) / 100 * 0.8)
    return {
      symbol: s,
      name: coin, pair: 'USDT',
      lastPrice: fmt(price), lastPriceFiat: fmt(price),
      change: fmtChange(change),
      high: fmt(high),
      low: fmt(low),
      volume: fmtVol(vol),
    }
  })

  // Futures volume multipliers (higher than spot — derivatives markets are larger)
  const futuresVolMultiplier: Record<string, number> = { BTC: 42.6, ETH: 21.3, XRP: 5.7, SOL: 9.4, AVAX: 3.1, DOGE: 6.8, BNB: 12.5, ADA: 4.2, DOT: 2.9, LINK: 3.6, LTC: 2.7, NEAR: 1.4, APT: 1.2, ATOM: 1.8, UNI: 2.0, OP: 1.3, ARB: 1.1, MATIC: 2.3 }
  // Coin-M volume multipliers (lower liquidity than USDT-M)
  const coinMVolMultiplier: Record<string, number> = { BTC: 18.9, ETH: 8.7, SOL: 3.2, BNB: 4.6, XRP: 1.8, DOGE: 2.1, ADA: 1.1, AVAX: 0.9 }
  // Slight premium/discount offsets for futures (fraction of 1%)
  const futuresPremium: Record<string, number> = { BTC: 1.00012, ETH: 1.00018, XRP: 0.99985, SOL: 1.00024, AVAX: 0.99972, DOGE: 1.00008, BNB: 1.00015, ADA: 0.99991, DOT: 1.00006, LINK: 1.00011, LTC: 0.99988, NEAR: 1.00020, APT: 1.00009, ATOM: 0.99995, UNI: 1.00013, OP: 1.00007, ARB: 0.99982, MATIC: 1.00010 }
  const coinMPremium: Record<string, number> = { BTC: 0.99978, ETH: 0.99965, SOL: 1.00032, BNB: 0.99988, XRP: 1.00041, DOGE: 0.99956, ADA: 1.00028, AVAX: 0.99971 }
  // Change offsets so futures don't mirror spot exactly
  const futuresChangeOffset: Record<string, number> = { BTC: 0.13, ETH: -0.22, XRP: 0.35, SOL: -0.18, AVAX: 0.27, DOGE: -0.09, BNB: 0.16, ADA: -0.31, DOT: 0.08, LINK: -0.14, LTC: 0.21, NEAR: -0.06, APT: 0.11, ATOM: -0.19, UNI: 0.05, OP: 0.23, ARB: -0.12, MATIC: 0.09 }
  const coinMChangeOffset: Record<string, number> = { BTC: -0.08, ETH: 0.19, SOL: -0.27, BNB: 0.11, XRP: -0.16, DOGE: 0.32, ADA: -0.14, AVAX: 0.22 }

  // USDT-M futures mirror the same pairs as spot (matches real Binance behavior —
  // most USDT-margined perpetuals exist for the same coins as their spot market).
  const usdtMSymbols = allSymbols
  // Coin-M (coin-margined) contracts are genuinely more limited on real exchanges —
  // only offered for higher-liquidity majors, not the full long-tail altcoin list.
  const coinMSymbols = ['BTC/USD', 'ETH/USD', 'SOL/USD', 'BNB/USD', 'XRP/USD', 'DOGE/USD', 'ADA/USD', 'AVAX/USD', 'LINK/USD', 'DOT/USD', 'LTC/USD', 'BCH/USD', 'UNI/USD', 'ATOM/USD', 'NEAR/USD', 'APT/USD', 'ARB/USD', 'OP/USD', 'FIL/USD', 'ETC/USD', 'ICP/USD', 'TRX/USD', 'SUI/USD', 'TON/USD', 'INJ/USD', 'RUNE/USD', 'AAVE/USD', 'MKR/USD', 'CRV/USD', 'SNX/USD']

  const usdtMTableData = usdtMSymbols.map(s => {
    const coin = s.split('/')[0]
    const spotData = marketData[`${coin}/USDT`] || {}
    const spotPrice = spotData.price || 0
    const price = spotPrice * (futuresPremium[coin] || 1)
    const change = (spotData.change24h || 0) + (futuresChangeOffset[coin] || 0)
    const vol = price * (futuresVolMultiplier[coin] || 2) * 1000000
    return {
      symbol: s, name: coin, pair: 'USDT Perp',
      lastPrice: fmt(price), lastPriceFiat: fmt(price),
      change: fmtChange(change),
      high: fmt(price * (1 + Math.abs(change || 1.8) / 100 * 1.15)),
      low: fmt(price * (1 - Math.abs(change || 1.8) / 100 * 0.85)),
      volume: fmtVol(vol),
    }
  })

  const coinMTableData = coinMSymbols.map(s => {
    const coin = s.split('/')[0]
    const spotData = marketData[`${coin}/USDT`] || {}
    const spotPrice = spotData.price || 0
    const price = spotPrice * (coinMPremium[coin] || 1)
    const change = (spotData.change24h || 0) + (coinMChangeOffset[coin] || 0)
    const vol = price * (coinMVolMultiplier[coin] || 1) * 1000000
    return {
      symbol: s, name: coin, pair: 'USD Perp',
      lastPrice: fmt(price), lastPriceFiat: fmt(price),
      change: fmtChange(change),
      high: fmt(price * (1 + Math.abs(change || 2.0) / 100 * 1.1)),
      low: fmt(price * (1 - Math.abs(change || 2.0) / 100 * 0.9)),
      volume: fmtVol(vol),
    }
  })

  const rawTableData = (activeTab === 'futures' || mobileMarketTab === 'futures')
    ? futuresSubTab === 'coin-m' ? coinMTableData
      : futuresSubTab === 'usdt-m' ? usdtMTableData
      : [...usdtMTableData, ...coinMTableData]
    : spotTableData

  const filteredTableData = rawTableData.filter(row =>
    !spotSearchQuery.trim() ||
    row.symbol.toLowerCase().includes(spotSearchQuery.trim().toLowerCase()) ||
    row.name.toLowerCase().includes(spotSearchQuery.trim().toLowerCase())
  )
  const tableData = filteredTableData.slice(0, spotVisibleCount)
  const spotHasMore = spotVisibleCount < filteredTableData.length

  // Facebook-style infinite scroll: auto-load more rows when the sentinel below
  // the table scrolls into view. Works for both Spot and Futures now — whichever
  // table is currently showing. The "Load more" button (rendered alongside the
  // sentinel) still works as an explicit fallback for anyone who prefers clicking.
  useEffect(() => {
    const node = spotSentinelRef.current
    if (!node || (activeTab !== 'spot' && activeTab !== 'futures') || !spotHasMore) return
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) {
        setSpotVisibleCount(c => c + SPOT_PAGE_SIZE)
      }
    }, { rootMargin: '200px' }) // start loading a bit before it's actually on-screen
    observer.observe(node)
    return () => observer.disconnect()
  }, [activeTab, spotHasMore, spotVisibleCount])

  // Reset the visible window whenever the active table changes (Spot ⇄ Futures,
  // or between All/USDT-M/Coin-M) so switching tabs doesn't carry over an
  // inflated count from scrolling through a different, larger list.
  useEffect(() => {
    setSpotVisibleCount(SPOT_PAGE_SIZE)
  }, [activeTab, mobileMarketTab, futuresSubTab])

  const mobileFunctions = [
    { icon: ClipboardList, label: 'Trade', color: '#5D5FEF', path: '/dashboard/trade' },
    { icon: Megaphone, label: 'Announcement', color: '#1B9B8C', path: '/dashboard/notifications' },
    { icon: Zap, label: 'Deposit', color: '#F59E0B', path: '/dashboard/deposit' },
    { icon: Activity, label: 'Futures', color: '#EF4444', path: '/dashboard/futures' },
    { icon: Library, label: 'Academy', color: '#1B9B8C', path: '/dashboard/academy' },
    { icon: Gift, label: 'Campaign', color: '#8B5CF6', path: '/dashboard/campaign' },
  ]

  const mobileSearchFilter = (s: string) =>
    !spotSearchQuery.trim() || s.toLowerCase().includes(spotSearchQuery.trim().toLowerCase())

  const spotMobileCoins = allSymbols.filter(mobileSearchFilter).slice(0, spotVisibleCount).map(s => {
    const coin = s.split('/')[0]
    const d = marketData[s] || {}
    const price = d.price || 0
    const vol = d.volume24h || (price * (mockVolMultiplier[coin] || 1) * 1000000)
    return {
      symbol: s, name: coin, pairSuffix: '/USDT',
      label: coinLabels[coin] || coin,
      price: fmt(price), rawPrice: price,
      change: fmtChange(d.change24h || 0), volume: fmtVol(vol),
      high: fmt(price * (1 + Math.abs(d.change24h || 1.5) / 100 * 1.2)),
      low: fmt(price * (1 - Math.abs(d.change24h || 1.5) / 100 * 0.8)),
      Logo: logoMap[coin] || (() => <div className="coin-icon-placeholder" style={{ backgroundColor: coinColors[coin] || '#333' }}>{coin[0]}</div>),
    }
  })

  const usdtMMobileCoins = usdtMSymbols.filter(mobileSearchFilter).slice(0, spotVisibleCount).map(s => {
    const coin = s.split('/')[0]
    const spotData = marketData[s] || {}
    const spotPrice = spotData.price || 0
    const price = spotPrice * (futuresPremium[coin] || 1)
    const change = (spotData.change24h || 0) + (futuresChangeOffset[coin] || 0)
    const vol = price * (futuresVolMultiplier[coin] || 2) * 1000000
    return {
      symbol: `${coin}/USDT`, name: coin, pairSuffix: '/USDT Perp',
      label: coinLabels[coin] || coin,
      price: fmt(price), rawPrice: price,
      change: fmtChange(change), volume: fmtVol(vol),
      high: fmt(price * (1 + Math.abs(change || 1.8) / 100 * 1.15)),
      low: fmt(price * (1 - Math.abs(change || 1.8) / 100 * 0.85)),
      Logo: logoMap[coin] || (() => <div className="coin-icon-placeholder" style={{ backgroundColor: coinColors[coin] || '#333' }}>{coin[0]}</div>),
    }
  })

  const coinMMobileCoins = coinMSymbols.filter(mobileSearchFilter).slice(0, spotVisibleCount).map(s => {
    const coin = s.split('/')[0]
    const spotData = marketData[`${coin}/USDT`] || {}
    const spotPrice = spotData.price || 0
    const price = spotPrice * (coinMPremium[coin] || 1)
    const change = (spotData.change24h || 0) + (coinMChangeOffset[coin] || 0)
    const vol = price * (coinMVolMultiplier[coin] || 1) * 1000000
    return {
      symbol: `${coin}/USD`, name: coin, pairSuffix: '/USD Perp',
      label: coinLabels[coin] || coin,
      price: fmt(price), rawPrice: price,
      change: fmtChange(change), volume: fmtVol(vol),
      high: fmt(price * (1 + Math.abs(change || 2.0) / 100 * 1.1)),
      low: fmt(price * (1 - Math.abs(change || 2.0) / 100 * 0.9)),
      Logo: logoMap[coin] || (() => <div className="coin-icon-placeholder" style={{ backgroundColor: coinColors[coin] || '#333' }}>{coin[0]}</div>),
    }
  })

  const mobileHasMore = (() => {
    const total = mobileMarketTab === 'futures'
      ? (futuresSubTab === 'coin-m' ? coinMSymbols : futuresSubTab === 'usdt-m' ? usdtMSymbols : usdtMSymbols.length + coinMSymbols.length)
      : allSymbols.length
    return spotVisibleCount < (typeof total === 'number' ? total : (total as any[]).length)
  })()
  const mobileSentinelRef = useRef<HTMLDivElement | null>(null)

  // Same infinite-scroll pattern as desktop, but with its own sentinel/ref since
  // the mobile list is a separate DOM subtree (CSS-hidden at desktop breakpoints,
  // not unmounted), so it can't share the desktop sentinel's ref node.
  useEffect(() => {
    const node = mobileSentinelRef.current
    if (!node || (mobileMarketTab !== 'spot' && mobileMarketTab !== 'futures') || !mobileHasMore) return
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) {
        setSpotVisibleCount(c => c + SPOT_PAGE_SIZE)
      }
    }, { rootMargin: '200px' })
    observer.observe(node)
    return () => observer.disconnect()
  }, [mobileMarketTab, mobileHasMore, spotVisibleCount])

  const mobileCoins = (mobileMarketTab === 'futures')
    ? futuresSubTab === 'coin-m' ? coinMMobileCoins
      : futuresSubTab === 'usdt-m' ? usdtMMobileCoins
      : [...usdtMMobileCoins, ...coinMMobileCoins]
    : spotMobileCoins

  const currentMobileTab = mobileMarketTab as string

  const toggleFavorite = (coin: string) => {
    setSelectedFavorites(prev => {
      const next = new Set(prev)
      if (next.has(coin)) next.delete(coin)
      else next.add(coin)
      return next
    })
  }

  const addAllFavorites = () => {
    setSelectedFavorites(new Set(favoritesData.map(a => a.coin)))
  }

  const removeAllFavorites = () => {
    setSelectedFavorites(new Set())
  }

  useEffect(() => {
    try {
      localStorage.setItem('bicoin_market_favorites', JSON.stringify([...selectedFavorites]))
    } catch { /* localStorage unavailable — favorites just won't persist this session */ }
  }, [selectedFavorites])

  return (
    <Layout activePage="market" hideFooterMobile={true}>
      <div className="market-refined-container">
        {/* Mobile View Start */}
        <div className="mobile-only">
          <>
              {/* Mobile Favorites View */}
              {mobileMarketTab === 'favorites' && (
                <div className="mobile-favorites-view">
                  <div className="mml-header-top">
                    <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ui-text-secondary)', display: 'flex', padding: 0 }}>
                      <ArrowLeft size={22} />
                    </button>
                    <span className="mml-title">Market</span>
                    <MoreVertical size={20} color="#ffffff" />
                  </div>

                  <div className="mml-tabs-container">
                    <div className="mml-main-tabs">
                      <button
                        className={`mml-tab-v3 ${currentMobileTab === 'favorites' ? 'active' : ''}`}
                        onClick={() => setMobileMarketTab('favorites')}
                      >
                        Favorites
                      </button>
                      <button
                        className={`mml-tab-v3 ${currentMobileTab === 'spot' ? 'active' : ''}`}
                        onClick={() => setMobileMarketTab('spot')}
                      >
                        Spot
                      </button>
                      <button
                        className={`mml-tab-v3 ${currentMobileTab === 'futures' ? 'active' : ''}`}
                        onClick={() => setMobileMarketTab('futures')}
                      >
                        Futures
                      </button>
                    </div>
                    <Search size={18} className="mml-search-icon" />
                  </div>

                  <div className="mobile-favorites-grid">
                    {favoritesData.map((asset, idx) => (
                      <div key={idx} className="mobile-fav-card" onClick={() => navigate(`/dashboard/trade?pair=${asset.coin}/USDT`)} style={{ cursor: 'pointer' }}>
                        <div className="mobile-fav-info">
                          <div className="mobile-fav-pair">
                            <span className="mobile-fav-coin">{asset.coin}</span>
                            <span className="mobile-fav-suffix"> /{asset.pair}</span>
                          </div>
                          <div className="mobile-fav-label">{asset.label}</div>
                        </div>
                        <div
                          className={`mobile-fav-check ${selectedFavorites.has(asset.coin) ? 'selected' : 'unselected'}`}
                          onClick={(e) => { e.stopPropagation(); toggleFavorite(asset.coin) }}
                          style={{ cursor: 'pointer' }}
                        >
                          {selectedFavorites.has(asset.coin) && <Check size={12} strokeWidth={3} />}
                        </div>
                      </div>
                    ))}
                  </div>

                  <button
                    className="mobile-add-all-btn"
                    onClick={() => { selectedFavorites.size === favoritesData.length ? removeAllFavorites() : addAllFavorites() }}
                  >
                    {selectedFavorites.size === favoritesData.length ? 'Remove all' : 'Add all'}
                  </button>
                </div>
              )}

              {/* Mobile Spot/Futures View */}
              {(mobileMarketTab === 'spot' || mobileMarketTab === 'futures') && (
                <div className="mobile-market-list-view">
                  <div className="mml-header-top">
                    <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ui-text-secondary)', display: 'flex', padding: 0 }}>
                      <ArrowLeft size={22} />
                    </button>
                    <span className="mml-title">Market</span>
                  </div>

                  <div className="mml-tabs-container">
                    <div className="mml-main-tabs">
                      <button
                        className={`mml-tab-v3 ${currentMobileTab === 'favorites' ? 'active' : ''}`}
                        onClick={() => setMobileMarketTab('favorites')}
                      >
                        Favorites
                      </button>
                      <button
                        className={`mml-tab-v3 ${currentMobileTab === 'spot' ? 'active' : ''}`}
                        onClick={() => setMobileMarketTab('spot')}
                      >
                        Spot
                      </button>
                      <button
                        className={`mml-tab-v3 ${currentMobileTab === 'futures' ? 'active' : ''}`}
                        onClick={() => setMobileMarketTab('futures')}
                      >
                        Futures
                      </button>
                    </div>
                    <Search size={20} className="mml-search-icon" />
                  </div>

                  <div className="mml-search-row">
                    <Search size={16} className="text-zinc-500" />
                    <input
                      type="text"
                      placeholder="Search for currency pairs"
                      className="mml-search-input"
                      value={spotSearchQuery}
                      onChange={(e) => { setSpotSearchQuery(e.target.value); setSpotVisibleCount(SPOT_PAGE_SIZE) }}
                    />
                  </div>

                  {mobileMarketTab === 'futures' && (
                    <div className="mml-futures-sub-nav">
                      <button
                        className={`mml-f-sub-tab ${futuresSubTab === 'all' ? 'active' : ''}`}
                        onClick={() => setFuturesSubTab('all')}
                      >
                        All
                      </button>
                      <button
                        className={`mml-f-sub-tab ${futuresSubTab === 'usdt-m' ? 'active' : ''}`}
                        onClick={() => setFuturesSubTab('usdt-m')}
                      >
                        USDT-M
                      </button>
                      <button
                        className={`mml-f-sub-tab ${futuresSubTab === 'coin-m' ? 'active' : ''}`}
                        onClick={() => setFuturesSubTab('coin-m')}
                      >
                        Coin-M
                      </button>
                    </div>
                  )}

                  <div className="mml-table-v2">
                    <div className="mml-table-header-v2">
                      <div className="mml-th-group">
                        <span className="mml-th">Name</span>
                        <span className="mml-th">Volume</span>
                      </div>
                      <div className="mml-th-group text-right">
                        <span className="mml-th">Last Price</span>
                        <span className="mml-th">24h change</span>
                      </div>
                    </div>

                    <div className="mml-list-v2">
                      {mobileCoins.map((coin, idx) => (
                        <div key={idx} className="mml-item-v2" onClick={() => navigate(`/dashboard/trade?pair=${coin.symbol}`)}>
                          <div className="mml-item-left-v2">
                            <Star size={16} className="mml-star-icon" onClick={(e) => { e.stopPropagation(); toggleFavorite(coin.name) }} style={{ color: selectedFavorites.has(coin.name) ? '#14B8A6' : undefined }} />
                            <div className="mml-coin-info-v2">
                              <div className="mml-pair-row">
                                <span className="mml-symbol-v2">{coin.name}</span>
                                <span className="mml-base-v2">{coin.pairSuffix}</span>
                              </div>
                              <span className="mml-volume-v2">{coin.volume}</span>
                            </div>
                          </div>
                          <div className="mml-item-right-v2">
                            <div className="mml-price-col">
                              <span className="mml-main-price-v2">{coin.price}</span>
                              <span className="mml-fiat-price-v2">{coin.price}</span>
                            </div>
                            <div className={`mml-change-badge-v2 ${coin.change.startsWith('+') ? 'positive' : 'negative'}`}>
                              {coin.change}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {mobileHasMore && (
                      <div ref={mobileSentinelRef} className="m-load-more">
                        <button
                          className="m-load-more-btn"
                          onClick={() => setSpotVisibleCount(c => c + SPOT_PAGE_SIZE)}
                        >
                          Load more
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
        </div>
        {/* Mobile View End */}

        <div className="desktop-only">
          {/* Top Feature Cards */}
          <div className="market-feature-row">
            {priceCards.map((card, idx) => (
              <div key={idx} className="m-feature-card">
                <div className="m-feature-header">
                  <div className="m-feature-coin">
                    <card.Logo />
                    <span className="m-feature-name">{card.coin} <span className="text-zinc-500">/{card.pair}</span></span>
                  </div>
                  <span className={`m-feature-change ${card.change.startsWith('+') ? 'positive' : 'negative'}`}>{card.change}</span>
                </div>
                <div className="m-feature-price">{card.price}</div>
                <div className="m-feature-footer">
                  <span className="m-feature-vol text-zinc-500">{card.volume}</span>
                  <PlayCircle size={16} className="text-zinc-300" />
                </div>
              </div>
            ))}
          </div>

          <div className="market-main-header">
            <h1 className="page-title">Market</h1>
            <p className="page-subtitle">Transfer your crypto assets into your Bicoin wallet</p>
          </div>

          {/* Main Tabs and Search */}
          <div className="market-controls-v2">
            <div className="market-tabs-v2">
              <button
                className={`m-tab-v2 ${activeTab === 'favorites' ? 'active' : ''}`}
                onClick={() => setActiveTab('favorites')}
              >
                Favorites
              </button>
              <button
                className={`m-tab-v2 ${activeTab === 'spot' ? 'active' : ''}`}
                onClick={() => setActiveTab('spot')}
              >
                Spot
              </button>
              <button
                className={`m-tab-v2 ${activeTab === 'futures' ? 'active' : ''}`}
                onClick={() => setActiveTab('futures')}
              >
                Futures
              </button>
            </div>
            <div className="m-search-v2">
              <Search size={18} className="text-zinc-500" />
              <input
                type="text"
                placeholder="Search for currency pairs"
                className="m-search-input"
                value={spotSearchQuery}
                onChange={(e) => { setSpotSearchQuery(e.target.value); setSpotVisibleCount(SPOT_PAGE_SIZE) }}
              />
            </div>
          </div>

          {/* Content Area */}
          {activeTab === 'favorites' && (
            <>
              {activeFavoritesData.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: '#71717A' }}>
                  No favorites yet — tap the star next to any pair in the Spot or Futures tab to add it here.
                </div>
              ) : (
              <div className="market-assets-grid-v2">
                {activeFavoritesData.map((asset, idx) => (
                  <div key={idx} className="m-asset-card" onClick={() => navigate(`/dashboard/trade?pair=${asset.coin}/USDT`)} style={{ cursor: 'pointer' }}>
                    <div className="m-asset-info">
                      <div className="m-asset-pair">
                        <span className="m-white-text">{asset.coin}</span>
                        <span className="text-zinc-500">/{asset.pair}</span>
                      </div>
                      <div className="m-asset-label text-zinc-500">{asset.label}</div>
                    </div>
                    <div className="m-asset-check">
                      <div
                        className="check-circle-teal"
                        onClick={(e) => { e.stopPropagation(); toggleFavorite(asset.coin) }}
                        style={{ cursor: 'pointer' }}
                        title="Remove from favorites"
                      >
                        <Check size={12} className="white" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              )}
              <div className="market-actions-v2">
                <button className="add-all-btn-v2" onClick={addAllFavorites}>Add all</button>
              </div>
            </>
          )}

          {(activeTab === 'spot' || activeTab === 'futures') && (
            <div className="market-table-section">
              {activeTab === 'futures' && (
                <div className="futures-sub-nav">
                  <button
                    className={`futures-sub-tab ${futuresSubTab === 'all' ? 'active' : ''}`}
                    onClick={() => setFuturesSubTab('all')}
                  >
                    All
                  </button>
                  <button
                    className={`futures-sub-tab ${futuresSubTab === 'usdt-m' ? 'active' : ''}`}
                    onClick={() => setFuturesSubTab('usdt-m')}
                  >
                    USDT-M
                  </button>
                  <button
                    className={`futures-sub-tab ${futuresSubTab === 'coin-m' ? 'active' : ''}`}
                    onClick={() => setFuturesSubTab('coin-m')}
                  >
                    Coin-M
                  </button>
                </div>
              )}

              <div className="m-table-container">
                <table className="m-refined-table">
                  <thead>
                    <tr>
                      <th className="text-left">Name <ChevronDown size={14} className="inline ml-1" /></th>
                      <th className="text-left">Last Price <ChevronDown size={14} className="inline ml-1" /></th>
                      <th className="text-left">24h change <ChevronDown size={14} className="inline ml-1" /></th>
                      <th className="text-left">24h High <ChevronDown size={14} className="inline ml-1" /></th>
                      <th className="text-left">24h Low <ChevronDown size={14} className="inline ml-1" /></th>
                      <th className="text-left">Volume <ChevronDown size={14} className="inline ml-1" /></th>
                      <th className="text-left">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isPricesLoading ? (
                      Array.from({ length: Math.min(SPOT_PAGE_SIZE, 8) }).map((_, idx) => (
                        <tr key={`skeleton-${idx}`}>
                          <td>
                            <div className="m-table-name-cell">
                              <span className="m-skeleton-bar" style={{ width: 16, height: 16, borderRadius: '50%' }} />
                              <span className="m-skeleton-bar" style={{ width: 70, height: 14, marginLeft: 8 }} />
                            </div>
                          </td>
                          <td><span className="m-skeleton-bar" style={{ width: 60, height: 14 }} /></td>
                          <td><span className="m-skeleton-bar" style={{ width: 50, height: 14 }} /></td>
                          <td><span className="m-skeleton-bar" style={{ width: 55, height: 14 }} /></td>
                          <td><span className="m-skeleton-bar" style={{ width: 55, height: 14 }} /></td>
                          <td><span className="m-skeleton-bar" style={{ width: 65, height: 14 }} /></td>
                          <td><span className="m-skeleton-bar" style={{ width: 40, height: 14 }} /></td>
                        </tr>
                      ))
                    ) : tableData.map((row, idx) => (
                      <tr key={idx}>
                        <td>
                          <div className="m-table-name-cell">
                            <Star
                              size={14}
                              className="cursor-pointer"
                              style={{ color: selectedFavorites.has(row.name) ? '#14B8A6' : '#71717A' }}
                              fill={selectedFavorites.has(row.name) ? '#14B8A6' : 'none'}
                              onClick={(e) => { e.stopPropagation(); toggleFavorite(row.name) }}
                            />
                            <span className="m-white-text font-bold ml-2">{row.name}</span>
                            <span className="text-zinc-500 text-xs ml-1">/ {row.pair}</span>
                          </div>
                        </td>
                        <td>
                          <div className="m-table-price-cell">
                            <span className="m-white-text font-bold">{row.lastPrice}</span>
                            <span className="text-zinc-500 text-xs ml-1">/{row.lastPriceFiat}</span>
                          </div>
                        </td>
                        <td>
                          <span className={row.change.startsWith('+') ? 'm-positive-text' : 'm-negative-text'}>{row.change}</span>
                        </td>
                        <td>
                          <span className="m-white-text">{row.high}</span>
                        </td>
                        <td>
                          <span className="m-white-text">{row.low}</span>
                        </td>
                        <td>
                          <span className="m-white-text">{row.volume}</span>
                        </td>
                        <td>
                          <button className="m-trade-link" onClick={() => navigate(`/dashboard/trade?pair=${row.symbol}`)}>Trade</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {(activeTab === 'spot' || activeTab === 'futures') && spotHasMore && (
                <div ref={spotSentinelRef} className="m-load-more">
                  <button
                    className="m-load-more-btn"
                    onClick={() => setSpotVisibleCount(c => c + SPOT_PAGE_SIZE)}
                  >
                    Load more
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  )
}

export default Market







